// Facebook integration guide component using content from integrating-with-facebook.md
import { AlertTriangle, CheckCircle, Facebook, Play, ArrowLeft, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface FacebookIntegrationGuideProps {
  onBack?: () => void;
}

export function FacebookIntegrationGuide({ onBack }: FacebookIntegrationGuideProps) {
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        {onBack && (
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center">
            <Facebook className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">How to Connect Rainfactory with Your Facebook Page</h1>
            <p className="text-muted-foreground">Set up Facebook integration to publish directly to your Facebook Page</p>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <Alert className="bg-yellow-50 border-yellow-200">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-800">
          <strong>Important:</strong> Facebook publishing requires proper <strong>Business Portfolio</strong> setup and <strong>admin rights</strong> to your Facebook Page.
        </AlertDescription>
      </Alert>

      {/* Prerequisites */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Prerequisites
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>Before integrating with Rainfactory, ensure the following:</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">1</Badge>
              <span>You are an <strong>admin of the Facebook Page</strong> you're connecting</span>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">2</Badge>
              <span>The Facebook Page is part of a <strong>Business Portfolio</strong> (formerly Facebook Business Manager)</span>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">3</Badge>
              <span>Your <strong>personal Facebook account</strong> has <strong>admin rights</strong> to that Business Portfolio</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            📘 Make sure you have the proper permissions before starting the integration process.
          </p>
        </CardContent>
      </Card>

      {/* Integration Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-blue-600" />
            Integration Steps
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">1</Badge>
              <h3 className="font-semibold">Open Rainfactory</h3>
            </div>
            <p className="ml-8 text-muted-foreground">Navigate to your <strong>Rainfactory homepage</strong>.</p>
          </div>

          <Separator />

          {/* Step 2 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">2</Badge>
              <h3 className="font-semibold">Go to Brand Settings</h3>
            </div>
            <div className="ml-8 space-y-2">
              <p className="text-muted-foreground">Navigate to your brand's settings page.</p>
              <p className="text-muted-foreground">Find the <strong>Facebook</strong> card.</p>
              <p className="text-muted-foreground">Click <strong>Connect</strong>.</p>
            </div>
          </div>

          <Separator />

          {/* Step 3 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">3</Badge>
              <h3 className="font-semibold">Go to Facebook Business</h3>
            </div>
            <div className="ml-8 space-y-2">
              <p className="text-muted-foreground">Click <strong>Go to Facebook Business</strong> in the prompt.</p>
              <p className="text-muted-foreground">Select the <strong>Business Portfolio</strong> you want to connect to Rainfactory.</p>
              <p className="text-muted-foreground">Click <strong>Continue</strong>.</p>
            </div>
          </div>

          <Separator />

          {/* Step 4 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">4</Badge>
              <h3 className="font-semibold">Select Facebook Page(s)</h3>
            </div>
            <div className="ml-8 space-y-2">
              <p className="text-muted-foreground">Choose the Facebook Page(s) you want Rainfactory to manage.</p>
              <p className="text-muted-foreground">Example: ✅ <code className="bg-gray-100 px-1 rounded">Urban Roosters</code></p>
              <p className="text-muted-foreground">Click <strong>Save</strong>.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Finalize Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-600" />
            Confirm Connection in Rainfactory
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p>Return to <strong>Rainfactory</strong>.</p>
          <p>Select the Facebook Page you just added (e.g., <code className="bg-gray-100 px-1 rounded">Urban Rooster</code>).</p>
          <p>The <strong>Facebook card</strong> will now appear under a section labeled <strong>Connected</strong> with a ✅ green checkmark.</p>
        </CardContent>
      </Card>

      {/* Success Message */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <h3 className="text-lg font-semibold text-green-800">You're Done!</h3>
          </div>
          <p className="text-green-700 mb-3">You can now:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-700"><strong>Create posts in Rainfactory</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-700"><strong>Preview how they will appear on your Facebook Page</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-700"><strong>Schedule posts for future</strong> or <strong>publish immediately</strong></span>
            </div>
          </div>
          <p className="text-sm text-green-600 mt-4">
            Example: Posts created in Rainfactory are published instantly to your <strong>Facebook Page</strong>.
          </p>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Troubleshooting Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4">If something isn't working, check:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Are you an <strong>admin of the Facebook Page</strong>?</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Is the Facebook Page part of a <strong>Business Portfolio</strong>?</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Does your personal account have <strong>admin rights</strong> to the Business Portfolio?</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Are all <strong>permissions granted</strong> through your Meta Business account?</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 